'use client';

import { useEffect, useMemo, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { InstantSearchServerState, useClearRefinements } from 'react-instantsearch-hooks';
import { history } from 'instantsearch.js/es/lib/routers';
import NotFound from 'components/commercetools-ui/organisms/not-found';
import ProductList from 'components/commercetools-ui/organisms/product/product-list-algolia';
import ProductListProvider, {
  useProductList,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/context';
import {
  FacetConfiguration,
  PriceConfiguration,
  ProductListingContent,
  ProductListingPage,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/types';
import InstantSearch from 'components/HOC/InstantSearch';
import usePath from 'helpers/hooks/usePath';
import { getLocalizationInfo } from 'project.config';
import LocalizedIndex from 'providers/algolia/localized-index';
import { TasticProps } from 'frontastic/tastics/types';
import useLocale from 'helpers/hooks/useLocale';
import useSlug from 'helpers/hooks/useSlug';
import { BreadCrumb } from 'shared/types/content/BreadCrumb';
import { UiState } from 'instantsearch.js';
import {
  attributeMapping,
  attributeMappingValue,
  stateMappingtoAttributes,
  stateMappingtoAttributesValue,
} from 'algolia/product-list';

export interface Props {
  slug: string;
  searchQuery?: string;
  facetsConfiguration: FacetConfiguration[];
  pricesConfiguration: PriceConfiguration[];
  serverUrl: string;
  serverState?: InstantSearchServerState;
  data: {
    dataSourceId: string;
    dataSource: Record<string, any>;
  };
}

function ProductListTastic({ categories, data, slug, algoliaData, indexName }: TasticProps<Props>) {
  const topAndBottomContent = data?.data?.dataSource?.topAndBottomContent;
  const breadCrumbs: BreadCrumb[] = data?.data?.dataSource?.breadCrumb || [];
  const badgeData = data?.data?.dataSource?.badgeData;
  const isCategoryPage = data?.data?.dataSource?.isCategoryPage || false;
  const productListingContent = data?.data?.dataSource?.productListingContent as ProductListingPage | undefined;
  const { href: currentBreadCrumbUrl = '' } = breadCrumbs[breadCrumbs.length - 1] || {};
  const { updateFacetsConfiguration, updatePricesConfiguration } = useProductList();
  // const slug = useSlug();
  const { ctLocale: locale } = useLocale();
  const BrandAndSubCategory = data?.data?.dataSource?.Brands?.body;
  const { currency } = getLocalizationInfo(locale);

  // const categorySlug = (slug as string)?.split('/').join('_');

  const categoryContent = productListingContent?.contents?.find(
    (content: ProductListingContent) => content?.slug === `/${slug}`,
  );
  const searchParams = useSearchParams();

  const searchQuery = searchParams.get('query') as string;

  const { refine: clearAllRefinements } = useClearRefinements();

  const { pathWithoutQuery, absolutePath } = usePath();

  const facetsConfiguration = useMemo<Record<string, FacetConfiguration>>(() => {
    return (data.facetsConfiguration ?? []).reduce(
      (acc, configuration) => ({
        ...acc,
        [configuration.key.replace(/\{currency\}/, currency)]: {
          type: configuration.type,
          label: configuration.label,
        } as FacetConfiguration,
      }),
      {},
    );
  }, [data.facetsConfiguration, currency]);

  const pricesConfiguration = useMemo<Record<string, PriceConfiguration>>(() => {
    return (data.pricesConfiguration ?? []).reduce(
      (acc, configuration) => ({
        ...acc,
        [configuration.key]: {
          ranges: configuration.ranges,
        } as PriceConfiguration,
      }),
      {},
    );
  }, [data.pricesConfiguration]);

  useLayoutEffect(() => {
    if (isCategoryPage && !window.location.href.includes(currentBreadCrumbUrl)) {
      window.history.replaceState({}, '', currentBreadCrumbUrl);
    }
  }, [isCategoryPage, currentBreadCrumbUrl]);

  useEffect(() => {
    updateFacetsConfiguration(facetsConfiguration);
    updatePricesConfiguration(pricesConfiguration);
  }, [facetsConfiguration, pricesConfiguration, updateFacetsConfiguration, updatePricesConfiguration]);

  useEffect(() => {
    clearAllRefinements();
  }, [clearAllRefinements, pathWithoutQuery]);

  // const isCategoryFoundOrSearchQueryExists = useMemo(() => {
  //   if (searchQuery) return true;
  //   if (!categorySlug) return true;

  //   return !!categories?.find((category) => {
  //     const flag = category.slug === categorySlug;
  //     if (flag) {
  //       return true;
  //     }
  //     return absolutePath.includes(category._url as string);
  //   });
  // }, [searchQuery, categories, categorySlug, absolutePath]);

  // if (!isCategoryFoundOrSearchQueryExists) return <NotFound />;

  return (
    <ProductList
      slug={slug as string}
      searchQuery={searchQuery}
      categories={categories}
      breadCrumbs={breadCrumbs}
      facetsConfiguration={facetsConfiguration}
      // BrandAndSubCategory={BrandAndSubCategory}
      categoryContent={categoryContent}
      algoliaData={algoliaData}
      indexName={indexName as string}
      badgeData={badgeData}
      topAndBottomContent={topAndBottomContent}
    />
  );
}
function getMatchingFacetValues(
  facet: string,
  values: string[],
  stateMappingtoAttributes: Record<string, string>,
  availableFacatKeyValues: Record<string, string[]>,
): string[] {
  const key = stateMappingtoAttributes[facet];
  if (!key || !availableFacatKeyValues[key]) return [];

  const availableValues = availableFacatKeyValues[key];

  return values
    ?.map((value) => availableValues?.find((av) => av?.toLowerCase() === value?.toLowerCase()))
    ?.filter((v): v is string => !!v); // Remove undefined values
}

function ProductListAlgoliaTastic({ data, ...props }: TasticProps<Props>) {
  // const breadCrumbs: BreadCrumb[] = [...((data as any)?.data?.dataSource?.breadCrumb || [])];
  const { algoliaData } = props;
  const { categoryName, categorySlug, refinmentSlug, availableFacatKeyValues, selectedFilters } = algoliaData; // we will use this code later to optimize the url without facet

  const categoryPageId = availableFacatKeyValues?.['categoryPageId'];
  // console.log('availableFacatKeyValues', availableFacatKeyValues);
  const { ctLocale: locale, getCurrency } = useLocale();
  let indexName: string = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US ?? '';
  // const hostName = typeof window !== 'undefined' ? window.location.hostname : '';
  // if (hostName?.includes('.eu') || hostName?.includes('-eu')) {
  if (getCurrency() !== 'GBP') {
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE ?? indexName;
  } else if (locale === 'en' || locale === 'en_GB') {
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US ?? indexName;
  } else {
    indexName = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE ?? indexName;
  }
  const categoryUrl = decodeURIComponent(categorySlug?.split('_')?.join('/'));

  // Build the URL from the UI state. When a sortBy value exists, it’s inserted into a dedicated URL segment.
  const routeStateToUrl = (routeState: UiState, location: Location) => {
    let newUrl = `/${categoryUrl}/`;
    const urlParts: string[] = [];
    Object.entries(routeState).forEach(([key, value]) => {
      if (key === 'sortBy') return; // Skip sortBy here
      let actualValue = Array.isArray(value) ? value.filter((item: string) => item !== '') : [];
      if (key === 'categories.en-GB.lvl1') {
        actualValue = actualValue?.map((item) => item?.split(' > ')?.[1]);
      }
      if (actualValue.length && key !== 'standalonePrice.priceGB' && key !== 'standalonePriceSort') {
        const formattedValue = actualValue.join(',').replaceAll(' ', '-').toLowerCase();
        if (formattedValue) {
          urlParts.push(`${attributeMapping[key]}/${formattedValue}`);
        }
      }
    });
    if (urlParts.length > 0) {
      newUrl += `shop-by/${urlParts[0]}/`;
      if (urlParts.length > 1) {
        newUrl += urlParts?.slice(1).join('/');
      }
    }
    // Append the sort-by segment if a sortBy value exists
    if (routeState.sortBy) {
      const sortByIndex = routeState?.sortBy as string;
      const URLSortBySlug = sortByIndex?.split(`${indexName}_`)[1];
      if (!newUrl.endsWith('/')) {
        newUrl += '/';
      }
      newUrl += `sort-by/${URLSortBySlug}/`;
    }
    console.log('newUrl', newUrl);
    return newUrl;
  };
  // Parse the URL to extract the UI state. This function extracts the sortBy value from the dedicated segment.
  const urlToRouteState = (url: string) => {
    let sortBy;
    const sortByMatch = url?.match(/\/sort-by\/([^/]+)\//);
    if (sortByMatch && sortByMatch[1]) {
      sortBy = `${indexName}_` + sortByMatch[1];
    }
    // Remove the sort-by segment for facet parsing
    const urlWithoutSortBy = url?.replace(/\/sort-by\/[^/]+\//, '/');
    const query = urlWithoutSortBy?.split('/shop-by/')[1]?.slice(0, -1);
    const refinementList: Record<string, string[]> = {};
    if (query) {
      const facets = query?.split('/');
      for (let i = 0; i < facets.length; i += 2) {
        const facet = facets[i];
        const values =
          decodeURIComponent(facets[i + 1])
            ?.toLowerCase()
            ?.replaceAll('-', ' ')
            ?.split(',') || [];

        //refinementList[stateMappingtoAttributes[facet]] = values;

        if (stateMappingtoAttributes[facet] === 'categories.en-GB.lvl1') {
          // const actualValues = categoryPageId?.filter(
          //     (item: string) => values?.filter((value) => item?.includes(value))?.length > 0,
          // );
          const categoriesResult: string[] = categoryPageId?.filter((item: string) => {
            if (!item?.includes('>') || item?.includes('brands')) return false;
            const formattedSplit = item?.split('>')[1]?.trim().toLowerCase();
            return values?.some((value: string) => formattedSplit === value?.replaceAll('-', ' '));
          });

          refinementList[stateMappingtoAttributes[facet]] = categoriesResult;
        } else {
          const facetedResult = getMatchingFacetValues(
            facet,
            values,
            stateMappingtoAttributes,
            availableFacatKeyValues,
          );
          if (facetedResult?.length > 0) {
            refinementList[stateMappingtoAttributes[facet]] = facetedResult;
          } else {
            refinementList[stateMappingtoAttributes[facet]] = values;
          }
        }
      }
    }
    const routeState: any = { ...refinementList };
    if (sortBy) {
      routeState.sortBy = sortBy;
    }
    return routeState;
  };
  // Define the router and state mapping for InstantSearch.
  const routing = {
    router: history({
      createURL({ qsModule, routeState, location }) {
        return `${location.origin}${routeStateToUrl(routeState, location)}`;
      },
      parseURL({ qsModule, location }): UiState {
        console.log('location', location.pathname);
        return urlToRouteState(location.pathname) as unknown as UiState;
      },
    }),
    stateMapping: {
      stateToRoute(uiState: { [key: string]: any }) {
        const indexUiState = uiState[indexName] || {};
        const { refinementList, range, sortBy } = indexUiState;
        let stateToRoute: { [key: string]: any } = {};
        if (refinementList) {
          Object.entries(refinementList).forEach(([facet, value]: any) => {
            if (facet && value && value.length > 0 && Array.isArray(value)) {
              stateToRoute[facet] = value.map((item: string) => item.toUpperCase().replaceAll('-', ' ')).sort();
            }
          });
        }
        // range &&
        //   Object.entries(range)?.forEach(([facet, value]: any) => {
        //     if (facet && value && value?.length > 0) {
        //       const formatedValue = value?.split(':');
        //       stateToRoute[facet] = [`${(formatedValue[0] / 100).toFixed(2)}-${(formatedValue[1] / 100).toFixed(2)}`];
        //     }
        //   });
        if (sortBy) {
          stateToRoute.sortBy = sortBy;
        }
        return stateToRoute;
      },
      routeToState(routeState: any) {
        console.log('routeState', routeState);
        const lve1Category = routeState['categories.en-GB.lvl1'];
        if (routeState && lve1Category?.length > 0) {
          const actualValues = categoryPageId?.filter(
            (item: string) => lve1Category?.filter((value: string) => item?.includes(value))?.length > 0,
          );
          console.log('routeStateActualValues', actualValues);
          routeState['categories.en-GB.lvl1'] = actualValues;
        }
        return {
          [indexName]: {
            refinementList: { ...routeState },
            sortBy: routeState.sortBy || undefined,
          },
        };
      },
    },
  };
  return (
    <InstantSearch categorySlug={categoryName} routing={routing} insights>
      <LocalizedIndex type="products">
        <ProductListProvider>
          <ProductListTastic data={{ ...data }} indexName={indexName} slug={categorySlug as string} {...props} />
        </ProductListProvider>
      </LocalizedIndex>
    </InstantSearch>
  );
}

export default ProductListAlgoliaTastic;
